import { FileImage, FileSpreadsheet, FileText, Globe, Presentation, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/cn'

type Kind = { label: string; icon: LucideIcon; tint: string }

const KINDS: Record<string, Kind> = {
  pdf: { label: 'PDF', icon: FileText, tint: 'from-[#ff6b5e] to-[#e0352b]' },
  docx: { label: 'DOCX', icon: FileText, tint: 'from-[#4c8dff] to-[#1d5fd6]' },
  pptx: { label: 'PPTX', icon: Presentation, tint: 'from-[#ff9f43] to-[#e66b0a]' },
  xlsx: { label: 'XLSX', icon: FileSpreadsheet, tint: 'from-[#34c77b] to-[#16964f]' },
  xlsm: { label: 'XLSM', icon: FileSpreadsheet, tint: 'from-[#34c77b] to-[#16964f]' },
  csv: { label: 'CSV', icon: FileSpreadsheet, tint: 'from-[#34c77b] to-[#16964f]' },
  'web page': { label: 'WEB', icon: Globe, tint: 'from-[#5ac8fa] to-[#0a84ff]' },
  png: { label: 'PNG', icon: FileImage, tint: 'from-[#bf5af2] to-[#8e3fd1]' },
  jpg: { label: 'JPG', icon: FileImage, tint: 'from-[#bf5af2] to-[#8e3fd1]' },
  jpeg: { label: 'JPG', icon: FileImage, tint: 'from-[#bf5af2] to-[#8e3fd1]' },
  webp: { label: 'WEBP', icon: FileImage, tint: 'from-[#bf5af2] to-[#8e3fd1]' },
}

const FALLBACK: Kind = { label: 'DOC', icon: FileText, tint: 'from-[#8e8e93] to-[#636366]' }

function fileKind(fileType: string | null | undefined): Kind {
  if (!fileType) return FALLBACK
  return KINDS[fileType.toLowerCase()] ?? { ...FALLBACK, label: fileType.toUpperCase().slice(0, 4) }
}

/** Rounded, gradient file tile (like a Finder icon) with the type underneath for larger sizes. */
export function FileBadge({
  fileType,
  size = 'md',
  className,
}: {
  fileType: string | null | undefined
  size?: 'sm' | 'md' | 'lg'
  className?: string
}) {
  const kind = fileKind(fileType)
  const Icon = kind.icon
  return (
    <span
      aria-hidden
      className={cn(
        'relative inline-flex shrink-0 flex-col items-center justify-center bg-gradient-to-b text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.25),0_4px_10px_-4px_rgb(0_0_0/0.35)]',
        kind.tint,
        size === 'sm' && 'size-8 rounded-[9px]',
        size === 'md' && 'size-10 rounded-[11px]',
        size === 'lg' && 'size-12 rounded-[13px]',
        className,
      )}
    >
      <Icon className={size === 'sm' ? 'size-4' : 'size-[18px]'} strokeWidth={2} />
      {size !== 'sm' && (
        <span className="mt-px text-[7.5px] font-bold leading-none tracking-wider opacity-90">{kind.label}</span>
      )}
    </span>
  )
}
